import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { CircularProgress, Typography, styled, useTheme } from "@mui/material";
import {
  BottomActionArea,
  BottomActionButton,
  FormBody,
  FormContentContainer,
  FormHeader,
  NavigateBeforeButton,
  SingleFormInput,
} from "@components";
import {
  NewLoginContactUsParagraph,
  NewLoginContactUsText,
  NewLoginErrorContainer,
  NewLoginErrorTitle,
  NewLoginErrorWrapper,
  StyledErrorIcon,
} from "../Auth/styled";
import { usePrescription } from "./hooks/usePrescription";
import { onContactUsClick } from "@utils";
import { prescriptionCodeErrorWsp } from "@constants";

import { FormContainer } from "@styles/styled";

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

type Inputs = {
  verificationCode: string;
};

export const PrescriptionCodeVerificationScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm<Inputs>();

  const verificationCode = watch("verificationCode");
  const { refetch, isFetching, isError } = usePrescription(verificationCode);
  const [contactUsMessage, setContactUsMessage] = useState("");

  const onSubmit: SubmitHandler<Inputs> = useCallback(async () => {
    const { data, isError } = await refetch();
    if (isError) {
      setError("verificationCode", {
        type: "manual",
        message: "",
      });
    }
    if (verificationCode) {
      setContactUsMessage(prescriptionCodeErrorWsp(verificationCode));
    }
    if (data) {
      navigate(verificationCode);
    }
  }, [verificationCode, refetch, setError, navigate]);

  const handleCodeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") {
        clearErrors("verificationCode");
        return;
      }
      if (!verificationCode) return;
      // Remove focus from input (to hide keyboard on mobile)
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      onSubmit({ verificationCode });
    },
    [verificationCode, clearErrors, onSubmit]
  );

  const handleBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleVerificationCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const upperValue = e.target.value.toUpperCase();
      setValue("verificationCode", upperValue);
    },
    [setValue]
  );

  return (
    <>
      <FormHeader
        useMekiDocIcon
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer>
        <FormBody>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <SingleFormInput
              title="Ingrese Código de Verificación"
              parameterName="verificationCode"
              errorText="Debes ingresar un código"
              register={register}
              errors={errors}
              inputType="text"
              onKeyDown={handleCodeKeyDown}
              onChange={handleVerificationCodeChange}
            />
            {!isError ? (
              <Subtitle>
                Para validar la receta y retenerla de ser necesario
              </Subtitle>
            ) : (
              <>
                {/* TODO: hacer un componente más reutilizable que tenga este
                tipo de errores */}
                <NewLoginErrorWrapper color={theme.palette.error.light}>
                  <NewLoginErrorContainer>
                    <StyledErrorIcon />
                    <NewLoginErrorTitle>
                      El código ingresado no es válido
                    </NewLoginErrorTitle>
                  </NewLoginErrorContainer>
                </NewLoginErrorWrapper>
                <NewLoginContactUsParagraph>
                  Si tienes problemas{" "}
                  <NewLoginContactUsText
                    onClick={() => onContactUsClick(contactUsMessage)}
                  >
                    contáctanos
                  </NewLoginContactUsText>
                </NewLoginContactUsParagraph>
              </>
            )}
            <BottomActionArea>
              <BottomActionButton
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={isFetching}
              >
                {isFetching ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verificar"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
